import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStores } from "stores/setup/use-store";
import { ProgressBar } from "pages/Alert/components/ProgressBar";
import { StepController } from "pages/Alert/components/StepController";
import { useEventCreationDrawer } from "./hooks/useEventCreationDrawer";
import { useEventCreationSubmission } from "./hooks/useEventCreationSubmission";
import { useAlertSteps } from "pages/Alert/hooks/useAlertSteps";
import { useFormValuesSync } from "pages/Alert/hooks/useFormValuesSync";
import { CreateEventFormValues } from "./types";
import { handleSubmitWithScroll } from "pages/Alert/util/handleSubmitWithScroll";
import { ChannelMismatchModal } from "pages/OceanWeatherIntelligence/InsightAlert/Components/ChannelMismatchModal";
import { useChannelValidation } from "pages/OceanWeatherIntelligence/InsightAlert/hooks/useChannelValidation";
import { useTranslation } from "react-i18next";
import { ILocationMenuItem } from "stores/data/operationRuleLocations/OperationRuleLocationModel";

interface EventCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  showPhoneVerificationModal: boolean;
  onPhoneModalOpen: () => void;
  onEventCreated?: () => void;
  prefilledLocation?: ILocationMenuItem | null;
  isLocationLocked?: boolean;
  prefilledDashboardId?: number;
  /** If true, insight block is always active and cannot be toggled off (used when creating from Event Manager) */
  forceInsightEnabled?: boolean;
  /** Initial step index to open the drawer at (0-indexed). Used when returning from sensor selection. */
  initialStep?: number;
}

export const EventCreateDrawer: React.FC<EventCreateDrawerProps> = observer(
  ({
    isOpen,
    onClose,
    onPhoneModalOpen,
    showPhoneVerificationModal,
    onEventCreated,
    prefilledLocation = null,
    isLocationLocked = false,
    prefilledDashboardId,
    forceInsightEnabled = false,
    initialStep = 0,
  }) => {
    const {
      t: tEvent,
      initialValues,
      validationSchema,
      resetInitialValues,
      handleDrawingMode,
      handlePointSelectionMode,
      handleGeoJsonLoaded,
      STEPS,
      selectedGeofence,
      environmentalVariables,
      selectedTemplate,
      clearGeofence,
      clearSelectedStation,
      setIsGeofenceLoadedByGeoJson,
      resetTemplateSelection,
      areas,
      handleAreaSelection,
      handleEnableAreaSelector,
      areaSelectorVisible,
      usersTableSelected,
      setUsersTableSelected,
      alertUsers,
    } = useEventCreationDrawer({
      isOpen,
      onEventCreatedSuccess: onPhoneModalOpen,
      showPhoneVerificationModal,
      prefilledLocation,
      isLocationLocked,
      prefilledDashboardId,
      forceInsightEnabled,
    });

    // Event submission hook
    const { handleEventSubmission, isSubmitting } = useEventCreationSubmission({
      onSuccessCallback: onPhoneModalOpen,
      showPhoneVerificationModal,
    });

    // Use insightAlert translations for shared components (Step1, Step2, Step3)
    const { t: tAlert } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.insightAlert",
    });

    // Combined translation function
    const t = useCallback(
      (key: string) => {
        // Check if key exists in event namespace first, fallback to insightAlert
        const eventTranslation = tEvent(key);
        if (
          eventTranslation !== key &&
          !eventTranslation.startsWith("event.")
        ) {
          return eventTranslation;
        }
        return tAlert(key);
      },
      [tEvent, tAlert],
    );

    const {
      dataStores: { insightAlertStore, forecastChartPageStore },
    } = useStores();

    const { checkChannelMismatch } = useChannelValidation();

    const {
      isOpen: isChannelMismatchModalOpen,
      onOpen: onChannelMismatchModalOpen,
      onClose: onChannelMismatchModalClose,
    } = useDisclosure();

    const [mismatchedChannels, setMismatchedChannels] = useState<string[]>([]);
    const [pendingSubmitValues, setPendingSubmitValues] =
      useState<CreateEventFormValues | null>(null);

    const {
      currentStep,
      nextStep,
      prevStep,
      resetSteps,
      goToStep,
      validateCurrentStep,
    } = useAlertSteps(STEPS.length);

    // Navigate to initial step when drawer opens (e.g., returning from sensor selection)
    // Uses a ref to ensure this only fires once per drawer open, not on every render
    const hasAppliedInitialStep = useRef(false);
    const returnedFromSensorSelection = useRef(false);
    useEffect(() => {
      if (isOpen && initialStep > 0 && !hasAppliedInitialStep.current) {
        hasAppliedInitialStep.current = true;
        returnedFromSensorSelection.current = true;
        goToStep(initialStep);
      }
      if (!isOpen) {
        hasAppliedInitialStep.current = false;
        returnedFromSensorSelection.current = false;
      }
    }, [isOpen, initialStep, goToStep]);

    // Ref for the drawer body to scroll to top when step changes
    const drawerBodyRef = useRef<HTMLDivElement>(null);
    // Ref to capture latest Formik values for flush-on-unmount
    const latestValuesRef = useRef<CreateEventFormValues | null>(null);
    // Track whether close was intentional (user action) vs unmount for selection mode
    const isIntentionalCloseRef = useRef(false);

    // Flush latest form values to store on unmount (e.g., when drawer unmounts for sensor selection)
    // This ensures the debounced useFormValuesSync doesn't lose pending data
    // Only flush if the unmount was NOT caused by an intentional close (which already resets the store)
    useEffect(() => {
      return () => {
        if (latestValuesRef.current && !isIntentionalCloseRef.current) {
          insightAlertStore.setCreateFormValues(latestValuesRef.current as any);
        }
      };
    }, [insightAlertStore]);

    // Scroll to top when step changes, or to sensor section when returning from sensor selection
    useEffect(() => {
      if (!drawerBodyRef.current) return;

      if (returnedFromSensorSelection.current) {
        returnedFromSensorSelection.current = false;

        // Use MutationObserver to wait for the sensor section to finish loading.
        // The section initially shows a Spinner; once data loads, it re-renders with
        // the actual content (SensorConfiguration). We watch for DOM changes and scroll
        // once the section is present and no longer contains a spinner.
        const drawerBody = drawerBodyRef.current;
        let scrolled = false;

        const scrollToSection = () => {
          const sensorSection = document.getElementById("sensor-alert-section");
          if (!sensorSection || !drawerBody) return false;

          // Check if section is still loading (contains a Chakra Spinner)
          const hasSpinner = sensorSection.querySelector(".chakra-spinner");
          if (hasSpinner) return false;

          const sectionRect = sensorSection.getBoundingClientRect();
          const drawerRect = drawerBody.getBoundingClientRect();
          const offsetInDrawer = sectionRect.top - drawerRect.top + drawerBody.scrollTop;
          drawerBody.scrollTo({ top: Math.max(0, offsetInDrawer - 16), behavior: "smooth" });
          return true;
        };

        // Try immediately in case content is already rendered
        if (!scrollToSection()) {
          let safetyTimer: ReturnType<typeof setTimeout>;

          const observer = new MutationObserver(() => {
            if (scrolled) return;
            if (scrollToSection()) {
              scrolled = true;
              observer.disconnect();
              clearTimeout(safetyTimer);
            }
          });

          observer.observe(drawerBody, { childList: true, subtree: true });

          // Safety timeout: disconnect observer after 5s to avoid leaks
          safetyTimer = setTimeout(() => {
            observer.disconnect();
            // Last-resort attempt
            if (!scrolled) scrollToSection();
          }, 5000);

          return () => {
            observer.disconnect();
            clearTimeout(safetyTimer);
          };
        }
      } else {
        drawerBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, [currentStep]);

    const handleClose = useCallback(() => {
      // Don't reset data when closing due to point/area selection mode
      const isInSelectionMode =
        forecastChartPageStore.isAreaDrawingMode ||
        forecastChartPageStore.isPointSelectionMode;

      isIntentionalCloseRef.current = true;
      onClose();
      resetSteps();

      if (!isInSelectionMode) {
        resetInitialValues();
        resetTemplateSelection();
      }
    }, [
      onClose,
      resetSteps,
      resetInitialValues,
      resetTemplateSelection,
      forecastChartPageStore,
    ]);

    // Close with success - calls onEventCreated callback
    const handleCloseWithSuccess = useCallback(() => {
      handleClose();
      onEventCreated?.();
    }, [handleClose, onEventCreated]);

    const handleNextStep = useCallback(
      async (
        validateForm: () => Promise<any>,
        setTouched: (touched: any, shouldValidate?: boolean) => void,
        setFieldError: (field: string, message: string | undefined) => void,
        values: any,
      ) => {
        if (
          insightAlertStore.isGeofenceInvalid ||
          insightAlertStore.isPointInvalid
        ) {
          return;
        }

        const isValid = await validateCurrentStep(
          currentStep,
          validateForm,
          setTouched,
          setFieldError,
          values,
        );

        if (isValid) {
          nextStep();
        }
      },
      [
        insightAlertStore.isGeofenceInvalid,
        insightAlertStore.isPointInvalid,
        validateCurrentStep,
        currentStep,
        nextStep,
      ],
    );

    const CurrentStepComponent = useMemo(
      () => STEPS[currentStep].component as React.ComponentType<any>,
      [STEPS, currentStep],
    );

    // Handler for category change - resets steps to avoid invalid step index
    const handleCategoryChange = useCallback(() => {
      resetSteps();
    }, [resetSteps]);

    const handleFormSubmit = useCallback(
      (values: CreateEventFormValues) => {
        // Only check channel mismatch if Block 2 (alerts) is active
        if (values.block2Active) {
          const mismatched = checkChannelMismatch(
            insightAlertStore.operationRuleAlertUserSelected,
            values.channels || [],
          );

          if (mismatched.length > 0) {
            setMismatchedChannels(mismatched);
            setPendingSubmitValues(values);
            onChannelMismatchModalOpen();
            return;
          }
        }

        // Submit the event (handles both Insight and Alert creation)
        handleEventSubmission(values, handleCloseWithSuccess);
      },
      [
        checkChannelMismatch,
        insightAlertStore.operationRuleAlertUserSelected,
        onChannelMismatchModalOpen,
        handleEventSubmission,
        handleCloseWithSuccess,
      ],
    );

    const handleChannelMismatchConfirm = useCallback(() => {
      if (pendingSubmitValues) {
        handleEventSubmission(pendingSubmitValues, handleCloseWithSuccess);
        setPendingSubmitValues(null);
      }
      onChannelMismatchModalClose();
    }, [
      pendingSubmitValues,
      handleEventSubmission,
      handleCloseWithSuccess,
      onChannelMismatchModalClose,
    ]);

    const handleChannelMismatchCancel = useCallback(() => {
      setPendingSubmitValues(null);
      onChannelMismatchModalClose();
    }, [onChannelMismatchModalClose]);

    const syncFormValues = useCallback(
      (values: CreateEventFormValues) => {
        insightAlertStore.setCreateFormValues(values);
      },
      [insightAlertStore],
    );

    // Custom StepController with Event-specific button text
    const renderStepController = useCallback(
      (
        validateForm: () => Promise<any>,
        setTouched: (touched: any, shouldValidate?: boolean) => void,
        setFieldError: (field: string, message: string | undefined) => void,
        submitForm: () => void,
        values: any,
      ) => {
        return (
          <Box px={6} py={4} borderTopWidth="1px">
            <StepController
              currentStep={currentStep}
              totalSteps={STEPS.length}
              onNext={() =>
                handleNextStep(validateForm, setTouched, setFieldError, values)
              }
              onBack={prevStep}
              onCancel={handleClose}
              onSubmit={() =>
                handleSubmitWithScroll({
                  initialValues,
                  validateForm,
                  setTouched,
                  submitForm,
                })
              }
              isNextDisabled={
                insightAlertStore.isGeofenceInvalid ||
                insightAlertStore.isPointInvalid
              }
              t={(key: string) => {
                // Override createAlert to createEvent
                if (key === "createAlert") {
                  return tEvent("createEvent");
                }
                return t(key);
              }}
              isSubmitting={isSubmitting}
            />
          </Box>
        );
      },
      [
        currentStep,
        STEPS.length,
        handleNextStep,
        prevStep,
        handleClose,
        initialValues,
        insightAlertStore.isGeofenceInvalid,
        insightAlertStore.isPointInvalid,
        t,
        tEvent,
        isSubmitting,
      ],
    );

    return (
      <>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="xl"
        >
          <Formik<CreateEventFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
            validateOnBlur={true}
            validateOnChange={false}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              submitForm,
              setFieldValue,
              setFieldError,
              validateForm,
              setTouched,
            }) => {
              useFormValuesSync({
                values: values as any, // Cast needed since CreateEventFormValues extends CreateAlertFormValues
                onSync: syncFormValues as any,
                isOpen,
                debounceMs: 300,
              });

              // Keep ref updated with latest values for flush-on-unmount
              latestValuesRef.current = values;

              return (
                <>
                  <DrawerOverlay />
                  <DrawerContent bg="white">
                    <DrawerCloseButton color={"black"} />
                    <DrawerHeader>
                      <Heading
                        display={"flex"}
                        size="md"
                        color="black"
                        gap="16px"
                      >
                        <Text mt={"3px"}>{tEvent("createEvent")}</Text>
                      </Heading>

                      <ProgressBar currentStep={currentStep} steps={STEPS} />
                    </DrawerHeader>

                    <DrawerBody ref={drawerBodyRef} color="black">
                      <Form onSubmit={handleSubmit}>
                        <Box>
                          <CurrentStepComponent
                            values={values}
                            errors={errors}
                            touched={touched}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            selectedGeofence={selectedGeofence}
                            selectedTemplate={selectedTemplate}
                            handleDrawingMode={handleDrawingMode}
                            handlePointSelectionMode={handlePointSelectionMode}
                            handleGeoJsonLoaded={handleGeoJsonLoaded}
                            environmentalVariables={environmentalVariables}
                            t={t}
                            showGeofenceError={
                              insightAlertStore.isGeofenceInvalid &&
                              insightAlertStore.isGeofenceLoadedByGeoJson
                            }
                            clearGeofence={clearGeofence}
                            clearSelectedStation={clearSelectedStation}
                            setIsGeofenceLoadedByGeoJson={
                              setIsGeofenceLoadedByGeoJson
                            }
                            areas={areas}
                            handleAreaSelection={handleAreaSelection}
                            handleEnableAreaSelector={handleEnableAreaSelector}
                            areaSelectorVisible={areaSelectorVisible}
                            usersTableSelected={usersTableSelected}
                            setUsersTableSelected={setUsersTableSelected}
                            alertUsers={alertUsers}
                            onCategoryChange={handleCategoryChange}
                            isLocationLocked={isLocationLocked}
                            forceInsightEnabled={forceInsightEnabled}
                            isDashboardLocked={
                              !!prefilledDashboardId && isLocationLocked
                            }
                            // Props for Step2 EventActionSelector
                            restrictionType={values.restrictionType}
                            onRestrictionTypeChange={(value: boolean) => {
                              setFieldValue("restrictionType", value);
                              // When switching to Permissive, clear title/description and reset templateType
                              // so the Step3 hook doesn't re-fill them on mount
                              if (!value) {
                                setFieldValue(
                                  "templateType",
                                  "no_category",
                                  false,
                                );
                                setFieldValue("customTitle", "", false);
                                setFieldValue("customDescription", "", false);
                              }
                            }}
                          />
                        </Box>
                      </Form>
                    </DrawerBody>

                    {renderStepController(
                      validateForm,
                      setTouched,
                      setFieldError,
                      submitForm,
                      values,
                    )}
                  </DrawerContent>
                </>
              );
            }}
          </Formik>
        </Drawer>

        <ChannelMismatchModal
          isOpen={isChannelMismatchModalOpen}
          onClose={handleChannelMismatchCancel}
          onConfirm={handleChannelMismatchConfirm}
          t={t}
          mismatchedChannels={mismatchedChannels}
        />
      </>
    );
  },
);

export default EventCreateDrawer;
